/**
 * Site Subscriptions Hook (multi-site)
 *
 * Parallel per-site subscription queries via `useQueries`. Each query is
 * keyed by `['sites', siteId, 'subscription']` so cache entries are shared
 * with `useSiteSubscription` — a site already fetched by either hook hits
 * the cache from the other.
 */

import { useQueries } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import { fetchSiteSubscription, SiteSubscription } from './useSiteSubscription';

export function useSiteSubscriptions(siteIds: string[]) {
  const { isAuthenticated, isSdkReady } = useAuth();

  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ['sites', siteId, 'subscription'],
      queryFn: () => fetchSiteSubscription(siteId),
      enabled: !!siteId && isAuthenticated && isSdkReady,
    })),
  });

  const subscriptions = new Map<string, SiteSubscription>();
  results.forEach((r, i) => {
    if (r.data) subscriptions.set(siteIds[i], r.data);
  });

  return {
    data: subscriptions,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
  };
}
